import { useState, useEffect } from 'react';

/**
 * Debounce hook to delay function execution
 * @param value - The value to debounce
 * @param delay - Delay in milliseconds
 * @returns Debounced value
 */
export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

/**
 * Debounce callback hook
 * @param callback - Function to debounce
 * @param delay - Delay in milliseconds
 * @returns Debounced function
 */
export function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): T {
  const [debouncedCallback, setDebouncedCallback] = useState<(() => void) | null>(null);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const debouncedFn = (...args: Parameters<T>) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => callback(...args), delay);
    };

    setDebouncedCallback(() => debouncedFn);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [callback, delay]);

  return debouncedCallback as T;
}
